import { GFORE_KR_SITE_IDS } from "@shared/common/data/gfore-kr-site-ids";
import type {
  GetCartInfoRes,
  GetCouponInfoRes,
  GetLoginUserRes,
  GetWishlistInfoRes,
  IOrderInfoRes,
  PageInfo,
} from "@shared/common/types/sdk";
import type { ExternalSiteType } from "@shared/common/types/site";
import { getSiteType } from "./get-site-type";

/**
 * 방문자 데이터 및 페이지 관련 데이터 로더 인터페이스
 */
export interface IVisitorDataLoader {
  /**
   * 사용자 로그인 유무를 반환한다.
   */
  getIsLogin(): Promise<boolean>;
  /**
   * 로그인 사용자 정보를 반환한다.
   * null을 반환하면 로그인되지 않은 상태이다.
   */
  getLoginUser(): Promise<GetLoginUserRes>;

  /**
   * 현재 페이지의 타입을 반환한다.
   *
   * @returns
   * - "PRODUCT": 상품 페이지
   * - "CATEGORY": 카테고리 페이지
   * - "CART": 장바구니 페이지
   * - "ORDER_SUCCESS": 주문 완료 페이지
   * - "UNKNOWN": 알 수 없는 페이지
   */
  getCurrentPageType(): Promise<PageInfo["type"]>;

  /**
   * 현재 페이지의 정보를 반환한다.
   *
   * @returns
   * - type: 페이지 타입
   * - [key: string]: 추가 정보
   */
  getCurrentPageInfo(): Promise<PageInfo>;

  getCartInfo(): Promise<GetCartInfoRes>;

  // TODO: 통화 단위 추가
  getOrderInfo(id: string): Promise<IOrderInfoRes>;

  getWishlistInfo(): Promise<GetWishlistInfoRes>;

  /** 현재 사용자가 지니고 있는 쿠폰 정보를 가져온다. */
  getCouponInfo(): Promise<GetCouponInfoRes>;

  /** 현재 페이지의 자세한 정보를 가져온다.
   * TODO: 이 함수는 임시방편이므로 언제든지 삭제될 수 있음. getCurrentPageInfo 에서 부하없이 페이지 정보를 가져오는 방도가 필요함. */
  getCurrentPageDetails?(): Promise<Record<string, unknown>>;

  siteType:
    | ExternalSiteType
    | "STAYFOLIO"
    | "ILOOM"
    | "GFORE_KR"
    | "HMALL"
    | "WELSTORY"
    | "UNKNOWN";
}

export const createVisitorDataLoader = async (
  siteId: string
): Promise<IVisitorDataLoader> => {
  // 일룸 전용 visitor data loader 로드
  if (siteId === "xvhxpliegq") {
    const loaderModule = await import(
      /* webpackChunkName: "iloom-user" */ "./site-iloom/visitor-data-loader"
    );
    return loaderModule.createIloomVisitorDataLoader();
  }
  // 스테이폴리오 전용 visitor data loader 로드
  else if (siteId === "ijqjntuwow") {
    const loaderModule = await import(
      /* webpackChunkName: "stayfolio-user" */ "./site-stayfolio/visitor-data-loader"
    );
    return loaderModule.createStayfolioVisitorDataLoader();
  }
  // 지포어 전용 visitor data loader 로드
  else if (GFORE_KR_SITE_IDS.includes(siteId)) {
    const loaderModule = await import(
      /* webpackChunkName: "gfore-kr-user" */ "./site-gfore-kr/visitor-data-loader"
    );
    return loaderModule.createGForeKrVisitorDataLoader();
  }
  // hamll 전용 visitor data loader 로드
  else if (["kbyfbdpmfm", "jjknhfisjp", "uddxbnioif"].includes(siteId)) {
    const loaderModule = await import(
      /* webpackChunkName: "hmall-user" */ "./site-hmall/visitor-data-loader"
    );
    return loaderModule.createHmallVisitorDataLoader();
  } else if (siteId === "cxymstsgdr") {
    const loaderModule = await import(
      /* webpackChunkName: "welstory-user" */ "./site-welstory/visitor-data-loader"
    );
    return loaderModule.createWelstoryVisitorDataLoader();
  }
  // 일반적인 경우
  const type = getSiteType();

  switch (type) {
    case "CAFE24": {
      const loaderModule = await import(
        /* webpackChunkName: "cafe24-user" */ "./cafe24/visitor-data-loader"
      );
      return loaderModule.createCafe24VisitorDataLoader();
    }
    case "IMWEB": {
      const loaderModule = await import(
        /* webpackChunkName: "imweb-user" */ "./imweb/visitor-data-loader"
      );
      return loaderModule.createImwebVisitorDataLoader();
    }
    case "GODOMALL": {
      const loaderModule = await import(
        /* webpackChunkName: "godomall-user" */ "./godomall/visitor-data-loader"
      );
      return loaderModule.createGodomallVisitorDataLoader();
    }
    case "SHOPIFY": {
      const loaderModule = await import(
        /* webpackChunkName: "shopify-user" */ "./shopify/visitor-data-loader"
      );
      return loaderModule.createShopifyVisitorDataLoader();
    }
    case "SIXSHOP_PRO": {
      const loaderModule = await import(
        /* webpackChunkName: "sixshop-pro-user" */ "./sixshop-pro/visitor-data-loader"
      );
      return loaderModule.createSixshopProVisitorDataLoader();
    }
    case "MAKESHOP": {
      const loaderModule = await import(
        /* webpackChunkName: "makeshop-user" */ "./makeshop/visitor-data-loader"
      );
      return loaderModule.createMakeshopVisitorDataLoader();
    }
    case "OOPY":
    case "SIXSHOP":
    case "WIX":
    case "SHOPBY":
    case "SHOPBY_PRO":
    case "WORDPRESS":
    case "NAVER_SMARTSTORE":
    case "UNKNOWN":
      return createDefaultVisitorDataLoader();
  }
};

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    CodenButterPageInfo?: PageInfo;
  }
}

export const createDefaultVisitorDataLoader = async (
  _args?: Record<string, string>
): Promise<IVisitorDataLoader> => {
  return {
    siteType: "UNKNOWN",
    async getIsLogin() {
      return false;
    },
    async getLoginUser() {
      return null;
    },
    async getCurrentPageType() {
      return window.CodenButterPageInfo?.type ?? "UNKNOWN";
    },
    async getCurrentPageInfo() {
      return window.CodenButterPageInfo ?? { type: "UNKNOWN" };
    },
    async getCartInfo() {
      return null;
    },
    async getOrderInfo(id) {
      return {
        id,
        amount: 0,
        count: 0,
        items: [],
      };
    },
    async getWishlistInfo() {
      return null;
    },
    async getCouponInfo() {
      return null;
    },
  };
};
